// The reading aids on a blog article (owner-approved design #59): a thin navy
// progress line under the header, an "In this article" contents panel built
// from the article's real h2/h3 headings, the section being read marked with a
// gold dot, and a smooth jump to a section on click.
//
// Everything here is progressive: the contents are plain `#id` links in the
// SSR markup (they work with no JS at all), and the headings carry a CSS
// scroll margin so a native jump also clears the sticky header.
import { useEffect, useRef, useState, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { CONTROL_EDGE, CONTROL_LIT } from "@majourneys/shared/ui";
import { Icon } from "@majourneys/icons";
import { panelStyle } from "../../../shared/ui/card-frame";
import { nextActiveIndex, readingProgress, type ArticleHeading } from "../lib/article-headings";

const NAVY = "#110E39";
const GOLD = "#E5B265";
const MUTED = "#5a5d77";
const NAVY_GRADIENT = "linear-gradient(135deg, #2E2878 0%, #1A1454 48%, #110E39 100%)";

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

/** The header's live height in page (zoomed) pixels; see `--mj-header-h`. */
function headerHeight(): number {
  if (typeof window === "undefined") return 68;
  const v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--mj-header-h"));
  return v > 0 ? v : 68;
}

/**
 * Jump to a section. `scrollIntoView` honours the heading's CSS
 * `scroll-margin-top` (header height + a breath), which is laid out inside the
 * zoomed body, so the browser does the 0.9 zoom arithmetic for us. Reduced
 * motion jumps without the glide.
 */
export function scrollToHeading(id: string): void {
  if (typeof document === "undefined") return;
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "start" });
  // Keep the hash shareable without a second (instant) jump.
  try {
    window.history.replaceState(window.history.state, "", `#${encodeURIComponent(id)}`);
  } catch {
    // history can be locked down in embedded browsers; the scroll still happened.
  }
}

/**
 * Which contents row is being read. One IntersectionObserver over the listed
 * headings, watching a band from just under the sticky header down to 35% of
 * the screen; `nextActiveIndex` turns each batch into the current row.
 */
export function useActiveSection(ids: readonly string[]): number {
  const [active, setActive] = useState(-1);
  const key = ids.join("|");

  useEffect(() => {
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined" || ids.length === 0) return;
    const els = ids.map((id) => document.getElementById(id)).filter((el): el is HTMLElement => !!el);
    if (els.length === 0) return;
    const indexOf = new Map(els.map((el) => [el, ids.indexOf(el.id)]));
    // rootMargin is in viewport pixels; the header height is in zoomed page
    // pixels, so scale it by the body zoom (1 on phones, 0.9 from 768px).
    const zoom = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--mj-ui-zoom")) || 1;
    const top = Math.round(headerHeight() * zoom) + 12;
    const io = new IntersectionObserver(
      (entries) => {
        setActive((prev) =>
          nextActiveIndex(
            prev,
            entries.map((e) => ({
              index: indexOf.get(e.target as HTMLElement) ?? -1,
              isIntersecting: e.isIntersecting,
              top: e.boundingClientRect.top,
              rootBottom: e.rootBounds?.bottom ?? Number.POSITIVE_INFINITY,
            })),
          ),
        );
      },
      { rootMargin: `-${top}px 0px -65% 0px`, threshold: 0 },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return active;
}

/**
 * The thin navy line under the header that fills as the article is read.
 * Scroll-driven, rAF-throttled, and it only ever writes `transform: scaleX()`.
 * The viewport height is read off a fixed full-height probe inside the zoomed
 * body, so it is in the same units as the article's rect at any zoom.
 */
export function ReadingProgress({ targetRef, isRtl }: { targetRef: RefObject<HTMLElement | null>; isRtl: boolean }) {
  const barRef = useRef<HTMLSpanElement>(null);
  const probeRef = useRef<HTMLSpanElement>(null);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    setReduced(prefersReducedMotion());
    let frame = 0;
    const update = () => {
      frame = 0;
      const target = targetRef.current;
      const bar = barRef.current;
      if (!target || !bar) return;
      const rect = target.getBoundingClientRect();
      const vh = probeRef.current?.getBoundingClientRect().height || window.innerHeight;
      const p = readingProgress(rect.top, rect.height, vh, headerHeight());
      bar.style.transform = `scaleX(${p.toFixed(4)})`;
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [targetRef]);

  return (
    <>
      <span ref={probeRef} aria-hidden className="pointer-events-none invisible fixed inset-y-0 start-0 w-px" />
      <div
        aria-hidden
        data-testid="reading-progress"
        className="pointer-events-none fixed inset-x-0 h-[3px]"
        style={{ top: "var(--mj-header-h, 68px)", zIndex: "var(--z-header, 999)" }}
      >
        <span
          ref={barRef}
          className="block h-full w-full"
          style={{
            background: NAVY_GRADIENT,
            transform: "scaleX(0)",
            transformOrigin: isRtl ? "right" : "left",
            // A short glide smooths wheel steps; reduced motion follows the
            // scroll exactly.
            transition: reduced ? "none" : "transform 120ms linear",
            willChange: "transform",
          }}
        />
      </div>
    </>
  );
}

function ContentsRows({
  entries,
  active,
  onPick,
}: {
  entries: readonly ArticleHeading[];
  active: number;
  onPick?: () => void;
}) {
  return (
    <ol className="flex flex-col gap-0.5">
      {entries.map((h, k) => {
        const current = k === active;
        const read = k < active;
        return (
          <li key={h.id}>
            <a
              href={`#${h.id}`}
              data-active={current ? "true" : undefined}
              aria-current={current ? "location" : undefined}
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                e.preventDefault();
                scrollToHeading(h.id);
                onPick?.();
              }}
              className={`flex min-h-[28px] w-full items-center gap-2 rounded-[10px] px-2 py-1.5 text-start font-sans text-[12.5px] leading-snug transition-colors duration-200 ${
                h.level === 3 ? "ps-5" : ""
              } ${current ? "font-semibold" : "hover:bg-white/70"}`}
              style={
                current
                  ? { background: CONTROL_LIT, boxShadow: `0 0 0 1px ${CONTROL_EDGE}`, color: NAVY }
                  : { color: read ? MUTED : NAVY }
              }
            >
              <span
                aria-hidden
                className="h-1.5 w-1.5 shrink-0 rounded-full transition-colors duration-200"
                style={{ background: k <= active ? GOLD : "rgba(17,14,57,0.15)" }}
              />
              <span className="line-clamp-2 min-w-0">{h.text}</span>
            </a>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Desktop: the "In this article" panel in the sticky column. Long lists
 * scroll inside the panel, and the current row is kept in view there.
 */
export function ContentsPanel({ entries, active }: { entries: readonly ArticleHeading[]; active: number }) {
  const { t } = useTranslation();
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const list = listRef.current;
    if (!list || active < 0) return;
    const row = list.querySelector<HTMLElement>('[data-active="true"]');
    if (!row) return;
    const top = row.offsetTop - list.offsetTop;
    if (top < list.scrollTop || top + row.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTo({ top: Math.max(0, top - list.clientHeight / 2), behavior: prefersReducedMotion() ? "auto" : "smooth" });
    }
  }, [active]);

  if (entries.length === 0) return null;
  return (
    <nav aria-label={t("blog.in_this_article")} className="p-2" style={panelStyle(18)}>
      <p className="px-2 pb-1.5 pt-2 font-sans text-[10.5px] font-bold uppercase tracking-[0.12em] text-[#5a5d77]">
        {t("blog.in_this_article")}
      </p>
      <div ref={listRef} className="relative max-h-[42vh] overflow-y-auto overscroll-contain">
        <ContentsRows entries={entries} active={active} />
      </div>
    </nav>
  );
}

/**
 * Phones: one "Contents" row at the top of the article that opens into the
 * same list. A `<details>` keeps every link in the SSR markup while shut;
 * picking a section folds it away again.
 */
export function ContentsFold({ entries, active }: { entries: readonly ArticleHeading[]; active: number }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDetailsElement>(null);
  if (entries.length === 0) return null;
  return (
    <details ref={ref} className="group p-2 lg:hidden" style={panelStyle(18)}>
      <summary className="flex min-h-[40px] cursor-pointer list-none items-center justify-between gap-3 px-2 [&::-webkit-details-marker]:hidden">
        <span className="font-sans text-[10.5px] font-bold uppercase tracking-[0.12em] text-[#5a5d77]">
          {t("blog.contents")}
        </span>
        <span className="inline-flex items-center gap-2 font-sans text-[11.5px] text-[#5a5d77]">
          {t("blog.sections_count", { count: entries.length })}
          <Icon
            name="ri-arrow-down-s-line"
            className="text-[16px] transition-transform duration-200 group-open:rotate-180"
            aria-hidden
          />
        </span>
      </summary>
      <nav aria-label={t("blog.in_this_article")} className="pb-1 pt-1">
        <ContentsRows
          entries={entries}
          active={active}
          onPick={() => {
            if (ref.current) ref.current.open = false;
          }}
        />
      </nav>
    </details>
  );
}
